// Visual-tell mapping. Defines how each trait or emergent is expressed in
// the renderer. Documenting them in one place is the v1 substitute for the
// sprite-composer system in phase 5 (which needs real art assets).
//
// Acceptance criterion 8: "every base trait has at least one visual tell".
// Direct (renderer-driven) tells today:
//   - size           -> radius (set at spawn, see spawn.ts)
//   - color (3 genes) -> body color (see spawn.colorForGenome)
//   - armor          -> outline thickness
//   - maxHealth      -> outline darkness (more health = darker rim)
//   - cohesion / separation / alignment -> flocking shape (emergent, not
//     drawn directly but visibly clusters/spreads the flock)
//   - speed          -> motion is the tell (faster balls move faster)
//   - attackRate     -> implicit (firing cadence)
//   - fertility      -> pink fertility ring while in window
// Plus all 10 emergent traits get a colored halo when present.

import type { EmergentEffect } from '../sim/traits/emergent';

/** Color halo for each named emergent trait. */
export const EMERGENT_HALO: Record<string, number> = {
  berserker: 0xff5050,
  'pack-leader': 0xffd44c,
  'glass-cannon': 0xc080ff,
  elder: 0xb8eaff,
  shepherd: 0x80ffd4,
  'iron-hide': 0x888888,
  frostbitten: 0x80c8ff,
  phoenix: 0xff8c40,
  pulsar: 0xffec5c,
  nursemaid: 0xf5b6ff,
};

/** Pick the most "distinctive" emergent's halo color, given a set of ids. */
export function haloColorFor(ids: readonly string[]): number | null {
  if (ids.length === 0) return null;
  // Priority order — rarest / most visible first.
  const priority = [
    'glass-cannon',
    'phoenix',
    'berserker',
    'pulsar',
    'pack-leader',
    'shepherd',
    'iron-hide',
    'elder',
    'frostbitten',
    'nursemaid',
  ];
  for (const id of priority) if (ids.includes(id)) return EMERGENT_HALO[id]!;
  return EMERGENT_HALO[ids[0]!] ?? null;
}

/** Outline thickness in pixels, scaled from armor stat. */
export function outlineWidthForArmor(armor: number): number {
  if (armor <= 0) return 1;
  if (armor >= 4) return 4;
  return Math.round(1 + armor);
}

/** Whether a ball should render its phoenix-ready aura (cosmetic flag). */
export function hasPhoenix(effect: EmergentEffect): boolean {
  return effect.phoenix === true;
}
